/**
 * Configuration structurelle du tutoriel interactif.
 *
 * Le texte de chaque étape vit dans les fichiers i18n (namespace game / GuidedTutorial)
 * sous `steps.<id>.title` et `steps.<id>.description`. Ici on ne décrit que la
 * mécanique : comment l'étape s'affiche et ce qui permet de passer à la suivante.
 */

/**
 * Condition d'avancement d'une étape :
 * - `next`         : l'utilisateur clique « Suivant » (étape purement informative).
 * - `building`     : un nouveau bâtiment vient d'être placé sur la carte.
 * - `staff`        : un personnel vient d'être recruté.
 * - `vehicle`      : un véhicule vient d'être acheté.
 * - `prise-appels` : la prise d'appels vient d'être activée.
 * - `intervention` : une intervention vient d'être clôturée.
 */
export type TutorialGate =
  | 'next'
  | 'building'
  | 'staff'
  | 'vehicle'
  | 'prise-appels'
  | 'intervention';

/** Événements de jeu remontés au moteur pour faire avancer les étapes « gated ». */
export type TutorialEvent = Exclude<TutorialGate, 'next'>;

export interface TutorialStep {
  /** Identifiant stable, sert aussi de clé i18n (`steps.<id>.*`). */
  id: string;
  /** Condition pour passer à l'étape suivante. */
  gate: TutorialGate;
  /**
   * `true`  → carte centrée avec fond assombri (intro / info / final).
   * `false` → coach flottant non bloquant : le joueur peut agir sur le jeu.
   */
  blocking: boolean;
  /** Id DOM d'un élément à mettre en surbrillance (spotlight) pendant l'étape. */
  anchor?: string;
  /** Autorise un bouton « Terminer / Continuer » même si l'étape est gated. */
  allowManualComplete?: boolean;
}

export const TUTORIAL_STEPS: TutorialStep[] = [
  // Introduction (bloquant)
  { id: 'welcome', gate: 'next', blocking: true },
  { id: 'map', gate: 'next', blocking: true },

  // Construire un bâtiment (action réelle sur la carte)
  { id: 'buildBuilding', gate: 'building', blocking: false },

  // Ouvrir la gestion d'un bâtiment (info) puis recruter / acheter (actions)
  { id: 'manageBuilding', gate: 'next', blocking: false, anchor: 'nav-buildings' },
  { id: 'recruitStaff', gate: 'staff', blocking: false },
  { id: 'buyVehicle', gate: 'vehicle', blocking: false },

  // Ouvrir la prise d'appels (action) → génère la première intervention
  { id: 'priseAppels', gate: 'prise-appels', blocking: false, anchor: 'map-toggle-prise-appels' },

  // Traiter l'intervention de tutoriel (action) — bouton de secours pour ne jamais bloquer
  { id: 'firstIntervention', gate: 'intervention', blocking: false, allowManualComplete: true },

  // Fin (bloquant)
  { id: 'complete', gate: 'next', blocking: true },
];

export const TUTORIAL_TOTAL_STEPS = TUTORIAL_STEPS.length;
